import React, { useMemo } from 'react'
import { matchPath } from 'react-router'
import { useAppSelector } from 'hooks/reduxHooks'
import SiteTakeoverOfferPageBanner from 'takeovers/utils/components/SiteTakeoverOfferPageBanner'
import SiteTakeoverPageBanner from 'takeovers/utils/components/SiteTakeoverPageBanner'
import SiteTakeoverSearchBanner from 'takeovers/utils/components/SiteTakeoverSearchBanner'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import SiteTakeoverPopup from './SiteTakeoverPopup'

/**
 * Page routes that would show this banner
 */
const homePageRoutes: Array<string> = [
  '/:regionCode',
]

const searchPageRoutes: Array<string> = [
  '/:regionCode/search',
  '/:regionCode/search/map',
  '/:regionCode/search/homes-and-villas',
  '/:regionCode/search/homes-and-villas/map',
]

const offerPageRoutes: Array<string> = [
  '/:regionCode/offer/:slug/:offerId',
  '/:regionCode/partner/:slug/:offerId',
  '/:regionCode/tour/:offerId',
  '/:regionCode/tour/:slug/:offerId',
  '/:regionCode/cruises/:offerId',
  '/:regionCode/cruises/:slug/:offerId',
]

/**
 * Page routes that would not show this banner
 */
const noBannerRoutes: Array<string> = [
  '/:regionCode/checkout',
  '/:regionCode/account',
  '/:regionCode/booking',
  '/:regionCode/bb/booking',
  '/:regionCode/trip-planner/generate-trip',
]

interface Props {
  vertical?: App.SiteTakeoverVertical
}
/**
 * This component is a wrapper for different banner types. Path matching to decide which banner type happens here.
 */
function SiteTakeoverTemplateBanner({ vertical }: Props) {
  const currentPath = useAppSelector(state => state.router.location.pathname)
  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const bannerContentPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableSiteTakeoverBannerContent?.enabled) {
      const payload = enableSiteTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableSiteTakeoverBannerContent])

  const shouldHide = useMemo(() => {
    return !!matchPath(currentPath, { path: noBannerRoutes })
  }, [currentPath])

  const currentPageType = useMemo((): 'home' | 'search' | 'offer' | 'generic' => {
    if (matchPath(currentPath, { path: offerPageRoutes })) {
      return 'offer'
    }
    if (matchPath(currentPath, { path: searchPageRoutes })) {
      return 'search'
    }
    if (matchPath(currentPath, { path: homePageRoutes, exact: true })) {
      return 'home'
    }
    return 'generic'
  }, [currentPath])

  if (shouldHide) {
    return null
  }

  // render if either the takeover banner content is enabled for that location
  // or the custom takeover banner is enabled for that location
  if (currentPageType === 'search') {
    return <SiteTakeoverSearchBanner vertical={vertical} />
  } else if (currentPageType === 'offer') {
    return <SiteTakeoverOfferPageBanner />
  } else if (currentPageType === 'generic') {
    if (bannerContentPayload?.genericBannerType === 'takeoverpopup' || bannerContentPayload?.genericBannerType === 'whitepopup') {
      return <SiteTakeoverPopup />
    } else {
      return <SiteTakeoverPageBanner />
    }
  }
  return null
}

export default SiteTakeoverTemplateBanner
